/* ═══════════════════════════════════════════════════════════════
   useTasksData — Tasks Kit's single data hook. Owns every load
   (Promise.allSettled), the specforma-sync-complete subscription, and
   every mutation (save task, status change, archive/restore, delete,
   assignments, project links, links, attachments, subtasks, comments,
   notifications) exactly as the former Tasks page did before the split.
   Components receive this hook's return value as props; none call
   TaskService directly.
   ═══════════════════════════════════════════════════════════════ */
import { useState, useEffect, useCallback } from 'react';
import type {
  Task,
  TaskAssignment,
  TaskComment,
  TaskLink,
  TaskAttachment,
  TaskProjectLink,
  TaskStatus,
  TaskPriority,
  TeamMember,
  Project,
  Team,
  TeamAssignment as TeamAssign,
} from '../../../shared/types';
import { TaskService } from '../../services/taskService';
import { TeamService } from '../../services/teamService';
import { ProjectService } from '../../services/projectService';
import { sanitizeProjectName } from '../projects/helpers';
import { listCached } from '../../services/cloudCache';
import { loadTasksBundle } from './taskDataLoader';

function generateId(): string {
  return 'task-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6);
}

/** Pure decision for the hook's `loadError` state — exported so the
 *  retry/error transition is unit-testable without a React test renderer
 *  (this repo has none — see lib/usePref.test.ts). `bundleRejected` is
 *  whether the REQUIRED tasks-bundle fetch failed on this attempt;
 *  `retry` is true on the first attempt (a silent retry is still
 *  pending — never surface an error yet), false on the final attempt.
 *  The 403-tolerant child-table fetches (members/projects/teams) never
 *  factor in here — only the primary tasks fetch drives this state. */
export function deriveLoadError(bundleRejected: boolean, retry: boolean): string | null {
  if (!bundleRejected) return null;
  if (retry) return null;
  return 'Tasks did not load. Check your connection and try again.';
}

export interface CreateTaskInput {
  title: string;
  description: string;
  priority: TaskPriority;
  status: TaskStatus;
  projectIds: string[];
  dueDate: string;
  assigneeIds: string[];
}

export interface SaveTaskDetailInput {
  title: string;
  description: string;
  priority: TaskPriority;
  status: TaskStatus;
  dueDate: string;
  projectIds: string[];
  assigneeIds: string[];
}

export function useTasksData(authUser: any, toast: (msg: string, isError?: boolean) => void) {
  const [tasks, setTasks] = useState<Task[]>(() => listCached<Task>('tasks'));
  const [assignments, setAssignments] = useState<TaskAssignment[]>(() =>
    listCached<TaskAssignment>('task_assignments')
  );
  const [members, setMembers] = useState<TeamMember[]>(() =>
    listCached<TeamMember>('team_members')
  );
  const [projects, setProjects] = useState<Project[]>(() => listCached<Project>('projects'));
  const [teams, setTeams] = useState<Team[]>(() => listCached<Team>('teams'));
  const [teamAssignments, setTeamAssignments] = useState<TeamAssign[]>(() =>
    listCached<TeamAssign>('team_assignments')
  );
  const [taskProjectLinks, setTaskProjectLinks] = useState<TaskProjectLink[]>(() =>
    listCached<TaskProjectLink>('task_project_links')
  );
  // Settles true after the projects load resolves (success or failure) —
  // gates the deep-link consumer so it never waits forever on an empty list.
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  // Set only when the REQUIRED tasks-bundle fetch fails after its retry —
  // lets TasksTable/TasksBoard show a distinct "did not load" state instead
  // of a false "no tasks" empty state. Cleared the moment the bundle loads.
  const [loadError, setLoadError] = useState<string | null>(null);

  const userId = authUser?.teamMemberId || authUser?.id || '';

  // ─── Load data ───
  const loadData = useCallback(
    async (retry = true) => {
      try {
        // Decision 24: tasks + assignments + project links load through
        // the ONE shared bundle loader, same as the project widgets and
        // the widget-builder's tasks source.
        const [bundle, m, p, tm, ta] = await Promise.allSettled([
          loadTasksBundle(),
          TeamService.list(),
          ProjectService.list(),
          TeamService.listTeams(),
          TeamService.listTeamAssignments(),
        ]);
        if (bundle.status === 'fulfilled') {
          setTasks(bundle.value.tasks);
          setAssignments(bundle.value.assignments);
          setTaskProjectLinks(bundle.value.links);
        }
        // Fresh arrays, not the cloudCache-owned reference these services
        // return (TeamService/ProjectService read straight from cloudCache's
        // list(), which is mutated in place on every save — see
        // taskDataLoader.ts for the same fix on the tasks bundle).
        if (m.status === 'fulfilled') setMembers([...m.value]);
        if (p.status === 'fulfilled') setProjects([...p.value]);
        if (tm.status === 'fulfilled') setTeams([...tm.value]);
        if (ta.status === 'fulfilled') setTeamAssignments([...ta.value]);

        const failures = [bundle, m, p, tm, ta].filter(
          (r): r is PromiseRejectedResult => r.status === 'rejected'
        );
        if (failures.length > 0) {
          console.error(
            'useTasksData loadData failed:',
            failures.map((f) => f.reason)
          );
          if (retry) setTimeout(() => loadData(false), 1500);
          else
            toast(
              'Failed to load tasks: ' + (failures[0].reason?.message || 'unknown error'),
              true
            );
        }
        setLoadError(deriveLoadError(bundle.status === 'rejected', retry));
      } catch (e: any) {
        console.error('useTasksData loadData failed:', e);
        if (retry) setTimeout(() => loadData(false), 1500);
        else toast('Failed to load tasks: ' + e.message, true);
        setLoadError(deriveLoadError(true, retry));
      } finally {
        setProjectsLoaded(true);
      }
    },
    [toast]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const retryLoad = useCallback(() => {
    void loadData();
  }, [loadData]);

  // Live-update: re-pull whenever cloudCache broadcasts a sync-complete
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, [loadData]);

  // ─── Helpers ───
  function getMemberName(id: string): string {
    return members.find((m) => m.id === id)?.name || 'Unknown';
  }
  function getProjectName(id?: string): string {
    if (!id) return '';
    return (
      sanitizeProjectName(projects.find((p) => p.id === id)?.name) ||
      projects.find((p) => p.id === id)?.projectNumber ||
      ''
    );
  }
  function getProjectIdsForTask(taskId: string): string[] {
    // Decision 20: task_project_links is the only project truth — the
    // deprecated task.projectId column is never read.
    const ids = new Set<string>();
    taskProjectLinks
      .filter((lnk) => lnk.taskId === taskId)
      .forEach((lnk) => ids.add(lnk.projectId));
    return Array.from(ids);
  }
  function getAssigneesForTask(taskId: string): TeamMember[] {
    const ids = assignments.filter((a) => a.taskId === taskId).map((a) => a.teamMemberId);
    return members.filter((m) => ids.includes(m.id));
  }
  function getSubtaskCount(taskId: string): number {
    return tasks.filter((t) => t.parentTaskId === taskId).length;
  }
  function getSubtasksDone(taskId: string): number {
    return tasks.filter((t) => t.parentTaskId === taskId && t.status === 'done').length;
  }

  // ─── Status change (drag or click) ───
  async function handleStatusChange(taskId: string, newStatus: TaskStatus) {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    try {
      const updated = { ...task, status: newStatus };
      // Decision 27: the server creates the status-change notification
      // (and emails, where applicable) off this same save — the client
      // no longer inserts task_notifications rows.
      await TaskService.saveTask(updated);

      await loadData();
    } catch (err: any) {
      toast('Could not update task: ' + (err?.message || 'Unknown error'), true);
    }
  }

  async function doArchiveTasks(items: Task[], archived: boolean) {
    try {
      for (const t of items) {
        await TaskService.setTaskArchived(t.id, archived, authUser?.displayName);
      }
      await loadData();
      toast(
        `${archived ? 'Archived' : 'Restored'} ${items.length} task${items.length !== 1 ? 's' : ''}`
      );
    } catch (err: any) {
      toast(
        `Those tasks could not be ${archived ? 'archived' : 'restored'}: ${err.message || 'Unknown error'}`,
        true
      );
    }
  }

  async function bulkDeleteTasks(items: Task[]) {
    for (const t of items) await TaskService.deleteTask(t.id);
    await loadData();
  }

  async function createTask(input: CreateTaskInput): Promise<boolean> {
    if (!input.title.trim()) {
      toast('Title is required', true);
      return false;
    }
    const task: Task = {
      id: generateId(),
      title: input.title.trim(),
      description: input.description.trim() || undefined,
      status: input.status,
      priority: input.priority,
      // Decision 20: task_project_links (below) is the only project truth —
      // the deprecated task.projectId column is never written.
      dueDate: input.dueDate || undefined,
      createdBy: userId,
      createdAt: '',
      updatedAt: '',
    };
    try {
      await TaskService.saveTask(task);

      for (const pid of input.projectIds) {
        await TaskService.saveTaskProjectLink({
          id: '',
          taskId: task.id,
          projectId: pid,
          createdAt: '',
        });
      }

      // Decision 27: the server sends the "assigned" notification (and
      // email) off this save — no client-side insert.
      for (const memberId of input.assigneeIds) {
        await TaskService.saveTaskAssignment({
          id: '',
          taskId: task.id,
          teamMemberId: memberId,
          assignedBy: userId,
          assignedAt: new Date().toISOString(),
        });
      }

      toast('Task created');
      await loadData();
      return true;
    } catch (err: any) {
      toast('Could not create task: ' + (err?.message || 'Unknown error'), true);
      return false;
    }
  }

  async function saveTaskDetail(task: Task, input: SaveTaskDetailInput): Promise<boolean> {
    const updated: Task = {
      ...task,
      title: input.title.trim(),
      description: input.description.trim() || undefined,
      priority: input.priority,
      status: input.status,
      dueDate: input.dueDate || undefined,
      // Decision 20: never write the deprecated projectId column — the
      // "Sync project links" block below is the only project truth, even
      // when `task` carried a legacy value.
      projectId: undefined,
    };
    try {
      await TaskService.saveTask(updated);

      // Sync project links
      const currentProjLinks = taskProjectLinks.filter((lnk) => lnk.taskId === task.id);
      const currentProjIds = currentProjLinks.map((lnk) => lnk.projectId);
      const projToAdd = input.projectIds.filter((pid) => !currentProjIds.includes(pid));
      const projToRemove = currentProjLinks.filter(
        (lnk) => !input.projectIds.includes(lnk.projectId)
      );
      for (const pid of projToAdd) {
        await TaskService.saveTaskProjectLink({
          id: '',
          taskId: task.id,
          projectId: pid,
          createdAt: '',
        });
      }
      for (const lnk of projToRemove) {
        await TaskService.deleteTaskProjectLink(lnk.id);
      }

      // Sync assignments
      const currentIds = assignments.filter((a) => a.taskId === task.id).map((a) => a.teamMemberId);
      const toAdd = input.assigneeIds.filter((id) => !currentIds.includes(id));
      const toRemove = currentIds.filter((id) => !input.assigneeIds.includes(id));

      // Decision 27: the server sends the "assigned" notification/email off
      // this save — no client-side insert.
      for (const memberId of toAdd) {
        await TaskService.saveTaskAssignment({
          id: '',
          taskId: task.id,
          teamMemberId: memberId,
          assignedBy: userId,
          assignedAt: new Date().toISOString(),
        });
      }
      for (const memberId of toRemove) {
        const ass = assignments.find((a) => a.taskId === task.id && a.teamMemberId === memberId);
        if (ass) await TaskService.deleteTaskAssignment(ass.id);
      }

      // Decision 27: a status change notification is created server-side
      // off the saveTask call above — no client-side insert here either.

      toast('Task updated');
      await loadData();
      return true;
    } catch (err: any) {
      toast('Could not update task: ' + (err?.message || 'Unknown error'), true);
      return false;
    }
  }

  async function deleteTask(taskId: string): Promise<void> {
    await TaskService.deleteTask(taskId);
    toast('Task deleted');
    await loadData();
  }

  async function addSubtask(parentTask: Task, title: string, projectId?: string): Promise<void> {
    if (!title.trim()) return;
    const sub: Task = {
      id: generateId(),
      title: title.trim(),
      status: 'todo',
      priority: parentTask.priority,
      parentTaskId: parentTask.id,
      createdBy: userId,
      createdAt: '',
      updatedAt: '',
    };
    await TaskService.saveTask(sub);
    // Decision 20: project association is a task_project_links row, never
    // the deprecated task.projectId column.
    if (projectId) {
      await TaskService.saveTaskProjectLink({
        id: '',
        taskId: sub.id,
        projectId,
        createdAt: '',
      });
    }
    await loadData();
    toast('Subtask added');
  }

  async function toggleSubtaskDone(sub: Task): Promise<void> {
    const newStatus: TaskStatus = sub.status === 'done' ? 'todo' : 'done';
    await TaskService.saveTask({ ...sub, status: newStatus });
    await loadData();
  }

  async function addComment(task: Task, text: string): Promise<void> {
    if (!text.trim()) return;
    await TaskService.saveTaskComment({
      id: '',
      taskId: task.id,
      authorId: userId,
      text: text.trim(),
      createdAt: '',
      updatedAt: '',
    });

    // Decision 27: the server notifies other assignees + the creator off
    // this comment insert — no client-side notification insert.

    toast('Comment added');
  }

  async function listComments(taskId: string): Promise<TaskComment[]> {
    return TaskService.listTaskComments(taskId);
  }

  async function addLink(taskId: string, url: string, label: string): Promise<TaskLink | null> {
    if (!url.trim()) return null;
    const link = await TaskService.saveTaskLink({
      id: '',
      taskId,
      url: url.trim(),
      label: label.trim() || undefined,
      createdAt: '',
    });
    toast('Link added');
    return link;
  }

  async function deleteLink(id: string): Promise<void> {
    await TaskService.deleteTaskLink(id);
    toast('Link removed');
  }

  async function listLinks(taskId: string): Promise<TaskLink[]> {
    return TaskService.listTaskLinks(taskId);
  }

  async function addAttachment(taskId: string): Promise<TaskAttachment | null> {
    const result = await TaskService.openFileDialog();
    if (!result) return null;
    const att = await TaskService.saveTaskAttachment({
      id: '',
      taskId,
      fileName: result.fileName,
      mimeType: result.mimeType,
      data: result.data,
      createdAt: '',
    });
    toast('Attachment added');
    return att;
  }

  async function deleteAttachment(id: string): Promise<void> {
    await TaskService.deleteTaskAttachment(id);
    toast('Attachment removed');
  }

  async function listAttachments(taskId: string): Promise<TaskAttachment[]> {
    return TaskService.listTaskAttachments(taskId);
  }

  return {
    tasks,
    assignments,
    members,
    projects,
    teams,
    teamAssignments,
    taskProjectLinks,
    projectsLoaded,
    loadError,
    retryLoad,
    userId,
    loadData,
    getMemberName,
    getProjectName,
    getProjectIdsForTask,
    getAssigneesForTask,
    getSubtaskCount,
    getSubtasksDone,
    handleStatusChange,
    doArchiveTasks,
    bulkDeleteTasks,
    createTask,
    saveTaskDetail,
    deleteTask,
    addSubtask,
    toggleSubtaskDone,
    addComment,
    listComments,
    addLink,
    deleteLink,
    listLinks,
    addAttachment,
    deleteAttachment,
    listAttachments,
  };
}

export type TasksData = ReturnType<typeof useTasksData>;
